import React, { useState } from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import {
  Analytics, Bolt, CheckCircleOutline, Description, ExpandMore, Explore, MoreHoriz,
  PersonAddAlt, Payments, ReceiptLong, ShoppingCart, Inventory2,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { s, cardSx, focusRing, reduceMotion } from './missionTokens';

/**
 * Quick Actions.
 *
 * The four verbs are the brief's action taxonomy. Each opens a real menu of
 * routes that exist — a dock full of dead buttons is worse than no dock, so
 * anything without a destination simply isn't listed.
 */
const GROUPS: { verb: string; icon: React.ElementType; tone: string; items: [string, string][] }[] = [
  {
    verb: 'Create', icon: ExpandMore, tone: s.primary,
    items: [
      ['Sales Order', '/sales/orders'],
      ['Purchase Request', '/procurement/requisitions'],
      ['Invoice', '/sales/invoices'],
      ['Payment', '/finance/payments'],
      ['Production Order', '/manufacturing/orders'],
      ['Journal Entry', '/finance/journals'],
    ],
  },
  {
    verb: 'Approve', icon: CheckCircleOutline, tone: s.success,
    items: [
      ['Purchase Orders', '/approvals'],
      ['Invoices', '/approvals'],
      ['Payments', '/approvals'],
      ['Leave Requests', '/approvals'],
      ['Expenses', '/approvals'],
    ],
  },
  {
    verb: 'Analyze', icon: Analytics, tone: s.violet,
    items: [
      ['Reports', '/reports'],
      ['Analytics', '/analytics'],
      ['Business Stories', '/reports'],
    ],
  },
  {
    verb: 'Navigate', icon: Explore, tone: s.info,
    items: [
      ['Finance', '/finance'],
      ['Procurement', '/procurement'],
      ['Inventory', '/inventory/stock'],
      ['Manufacturing', '/manufacturing/orders'],
      ['HRMS', '/hrms/employees'],
    ],
  },
];

const SHORTCUTS: [string, React.ElementType, string][] = [
  ['Sales Order', ShoppingCart, '/sales/orders'],
  ['Purchase Request', Description, '/procurement/requisitions'],
  ['GRN', Inventory2, '/procurement/grn'],
  ['Invoice', ReceiptLong, '/sales/invoices'],
  ['Payment', Payments, '/finance/payments'],
  ['Add Employee', PersonAddAlt, '/hrms/employees'],
];

const QuickActionDock: React.FC = () => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [openVerb, setOpenVerb] = useState<string | null>(null);

  const group = GROUPS.find((g) => g.verb === openVerb);

  return (
    <Box sx={{ ...cardSx, p: 1.75, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Bolt sx={{ fontSize: 20, color: s.primary }} />
        <Box>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: s.heading, lineHeight: 1.25 }}>
            Quick Actions
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: s.muted }}>Do more, faster.</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {GROUPS.map(({ verb, icon: Icon, tone }) => (
          <Box
            key={verb}
            component="button"
            aria-haspopup="menu"
            aria-expanded={openVerb === verb}
            onClick={(e: React.MouseEvent<HTMLElement>) => { setAnchor(e.currentTarget); setOpenVerb(verb); }}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.75, py: 0.9,
              cursor: 'pointer', borderRadius: 2, border: `1px solid ${tone}2e`,
              backgroundColor: `${tone}12`, color: tone, fontSize: '0.8rem', fontWeight: 700,
              transition: 'background-color .2s ease, transform .2s ease',
              '&:hover': { backgroundColor: `${tone}20`, transform: 'translateY(-1px)' },
              ...focusRing, ...reduceMotion,
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
            {verb}
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1, minWidth: 8 }} />

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {SHORTCUTS.map(([label, Icon, href]) => (
          <Box
            key={label}
            component="button"
            onClick={() => navigate(href)}
            title={label}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35,
              width: 74, px: 0.5, py: 0.85, cursor: 'pointer', borderRadius: 2,
              border: '1px solid transparent', background: 'none',
              transition: 'background-color .2s ease, border-color .2s ease',
              '&:hover': { backgroundColor: s.canvas, borderColor: s.border },
              ...focusRing, ...reduceMotion,
            }}
          >
            <Icon sx={{ fontSize: 17, color: s.body }} />
            <Typography sx={{ fontSize: '0.62rem', color: s.muted, fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>
              {label}
            </Typography>
          </Box>
        ))}
        <Box
          component="button"
          onClick={() => navigate('/reports')}
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35,
            width: 54, px: 0.5, py: 0.85, cursor: 'pointer', borderRadius: 2,
            border: '1px solid transparent', background: 'none',
            '&:hover': { backgroundColor: s.canvas, borderColor: s.border },
            ...focusRing,
          }}
        >
          <MoreHoriz sx={{ fontSize: 17, color: s.body }} />
          <Typography sx={{ fontSize: '0.62rem', color: s.muted, fontWeight: 600 }}>More</Typography>
        </Box>
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor && group)}
        onClose={() => { setAnchor(null); setOpenVerb(null); }}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 210, boxShadow: s.shadowLg } } }}
      >
        {group?.items.map(([label, href]) => (
          <MenuItem
            key={label}
            onClick={() => { navigate(href); setAnchor(null); setOpenVerb(null); }}
            sx={{ fontSize: '0.82rem', fontWeight: 500, color: s.heading, py: 1 }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default QuickActionDock;
